import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button, CircularProgress, Container, Pagination, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { format } from 'date-fns';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { EStatusCode } from '../api/enum';
import { IPaginatedResponse } from '../api/httpClient';
import { IGetProjectPreviewResponse } from '../api/project/iApiResponse';
import * as ProjectHelper from '../api/project/projectHelper';
import { route } from '../route/Index';
import { toastAlert, dialogAsync } from '../shared/util';
import { useAppSelector } from '../store/hook';
import { selectUser } from '../store/user/userSlice';

interface IHomeProps {}

const HomeContainer = styled(Container)`
    padding: 0;
    flex-grow: 1;
    margin-top: 1rem;
`;

const Home = (props: IHomeProps) => {
    let pageSize = 7;

    const { t } = useTranslation();

    const navigate = useNavigate();

    const user = useAppSelector(selectUser);

    const [projectPreview, setProjectPreview] = React.useState<IPaginatedResponse<IGetProjectPreviewResponse>>();
    const [isLoading, setIsLoading] = React.useState(false);
    const [pageNumber, setpageNumber] = React.useState(1);

    const fetchProjectPreview = async (page: number) => {
        setIsLoading(true);
        let res = await ProjectHelper.getProjectPreviewAsync(user.data!.email, page, pageSize);
        setProjectPreview(res.data);
        setIsLoading(false);

        if (res.status == 204) {
            navigate(route.info);
        }
    };

    React.useEffect(() => {
        fetchProjectPreview(pageNumber);
    }, [pageNumber]);

    const handleGoToProject = (id: number) => navigate(route.design.replace(':projectId', id.toString()));

    const handleRemoveProject = (id: number) => {
        dialogAsync('warning', t('areYouSure'), t('actionNotBeUndone'), async () => {
            let res = await ProjectHelper.deleteProjectAsync(id, user.data?.email!);
            if (res.status == EStatusCode.Ok) {
                toastAlert('success', t('deleted'), t('yourProjectHasBeenDeleted'));
                fetchProjectPreview(pageNumber);
            }
        });
    };

    return (
        <HomeContainer maxWidth='md'>
            <Stack height='100%' direction='column' spacing={4}>
                <Stack direction='row' justifyContent='space-between'>
                    <Typography variant='h1' gutterBottom>
                        {t('recentProjects')}
                    </Typography>
                    <Button variant='orange' endIcon={<AddCircleIcon />} onClick={() => navigate(route.info)}>
                        {t('addNewProject')}
                    </Button>
                </Stack>
                {isLoading && <CircularProgress />}
                {!isLoading && projectPreview?.value?.length! > 0 && (
                    <>
                        <Stack
                            flexGrow={1}
                            sx={{
                                display: { xs: 'none', md: 'block' },
                            }}>
                            <TableContainer>
                                <Table sx={{ minWidth: 650 }} size='small' aria-label='simple table'>
                                    <TableHead variant='greenLightWithoutBorder'>
                                        <TableRow>
                                            <TableCell>{t('name')}</TableCell>
                                            <TableCell align='center'>{t('lastUpdate')}</TableCell>
                                            <TableCell align='right'></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {projectPreview!.value.map(x => (
                                            <TableRow key={x.title} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                <TableCell onClick={() => handleGoToProject(x.id)} component='th' scope='row'>
                                                    {x.title}
                                                </TableCell>
                                                <TableCell onClick={() => handleGoToProject(x.id)} align='center'>
                                                    {format(x.lastUpdateDate, 'dd MMMM yyyy')}
                                                </TableCell>
                                                <TableCell onClick={() => handleRemoveProject(x.id)} align='right'>
                                                    <DeleteIcon />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Stack>
                        <Stack
                            flexGrow={1}
                            sx={{
                                display: { xs: 'block', md: 'none' },
                            }}>
                            <TableContainer>
                                <Table size='small' aria-label='simple table'>
                                    <TableHead variant='greenLight'>
                                        <TableRow>
                                            <TableCell>{t('name')}</TableCell>
                                            <TableCell align='right'></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {projectPreview!.value.map((x, i) => (
                                            <TableRow key={`${x.title}_${i}`} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                <TableCell onClick={() => handleGoToProject(x.id)} component='th' scope='row'>
                                                    <Stack spacing={1}>
                                                        <span style={{ fontWeight: 600, fontSize: '1.1rem' }}>{x.title}</span>
                                                        <span>{format(x.lastUpdateDate, 'dd MMMM yyyy')}</span>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell onClick={() => handleRemoveProject(x.id)} align='right'>
                                                    <DeleteIcon />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Stack>
                        {projectPreview!.pageCount > 1 && (
                            <Stack alignItems='end'>
                                <Pagination
                                    showFirstButton
                                    showLastButton
                                    variant='outlined'
                                    shape='rounded'
                                    page={projectPreview?.pageNumber}
                                    count={projectPreview?.pageCount}
                                    onChange={(e, value) => setpageNumber(value)}
                                />
                            </Stack>
                        )}
                    </>
                )}
                {!isLoading && projectPreview?.value?.length! == 0 && <div>Empty</div>}
            </Stack>
        </HomeContainer>
    );
};

export default Home;
